/**
 * Consuntivi del ledger incentivazione — contratti di RISPOSTA condivisi
 * BE↔FE (schema Zod + `z.infer`, docs/zod-boundary.md § Risposte).
 *
 * Due letture, entrambe sul principio "il ricalcolo scrive, il report legge":
 *
 * 1. Summary PER ISTANZA (`GET /incentivazione/ledger/competitions/:id`):
 *    aggregato delle sole righe evento per configuratore (KPI live del canvas).
 *
 * 2. Consuntivo PER DRIVER (`GET /incentivazione/ledger/competitions/:id/by-driver`):
 *    il totale economico della gara letto come Σ € per driver di business
 *    (ognuno = Σ dei suoi configuratori) + € dei configuratori extra-driver
 *    (istanze con `business_driver_id` NULL). L'importo per driver NON è mai
 *    configurato: è DERIVATO dal ledger, con due fonti sommate in colonne
 *    distinte e mai mescolate:
 *      - `event_*`     → righe evento (`commissioning_event_lines`);
 *      - `aggregate_*` → risultati di periodo correnti
 *        (`commissioning_aggregate_results`, is_current): la QUOTA INCREMENTALE
 *        prodotta da soglie/ranking/checkpoint/bonus-malus rispetto alla base
 *        ledger già rappresentata dalle righe evento (mai la base due volte).
 *    Maturato = confirmed+recredited (righe) / confirmed (aggregati);
 *    WIP = wip; reversed/pending/not_evaluable/excluded esclusi.
 *    Nessun filtro per sorgente evento: sell-out, sell-in e CRM pesano uguale.
 */
import { z } from 'zod';
import { COMMISSIONING_COMPETITION_FAMILIES, COMMISSIONING_EVENT_LINE_STATUSES } from '../types/commissioning';
import { COMMISSIONING_FUNCTION_OUTCOMES } from '../types/commissioning-kernel';
import { COMMISSIONING_AGGREGATE_RESULT_KINDS, COMMISSIONING_AGGREGATE_RESULT_STATUSES } from '../types/commissioning-runtime-v3';

// ---------------------------------------------------------------------------
// 1) Summary per istanza (righe evento)
// ---------------------------------------------------------------------------

/** Riga KPI live per istanza (righe evento aggregate per configuratore). */
export const commissioningLedgerInstanceSummarySchema = z.object({
  configurator_instance_id: z.string(),
  instance_name: z.string().nullable(),
  lines_total: z.number().int().nonnegative(),
  lines_wip: z.number().int().nonnegative(),
  lines_confirmed: z.number().int().nonnegative(),
  lines_reversed: z.number().int().nonnegative(),
  lines_recredited: z.number().int().nonnegative(),
  valenza_confirmed: z.number().finite(),
  valenza_wip: z.number().finite(),
  gettone_gara_confirmed: z.number().finite(),
  amount_eur_confirmed: z.number().finite(),
  amount_eur_wip: z.number().finite(),
  last_line_at: z.string().nullable(),
}).strict();
export type CommissioningLedgerInstanceSummary =
  z.infer<typeof commissioningLedgerInstanceSummarySchema>;

/** Risposta GET /incentivazione/ledger/competitions/:id. */
export const commissioningCompetitionLedgerSummarySchema = z.object({
  competition_id: z.string(),
  family: z.enum(COMMISSIONING_COMPETITION_FAMILIES),
  instances: z.array(commissioningLedgerInstanceSummarySchema),
}).strict();
export type CommissioningCompetitionLedgerSummary =
  z.infer<typeof commissioningCompetitionLedgerSummarySchema>;

// ---------------------------------------------------------------------------
// 2) Consuntivo per driver
// ---------------------------------------------------------------------------

/**
 * Driver, driver × configuratore, driver × cluster o driver × membro.
 * Cluster e membro sommano TUTTI i configuratori dello stesso driver.
 */
export const COMMISSIONING_DRIVER_SUMMARY_GRAINS = [
  'driver',
  'driver_instance',
  'driver_cluster',
  'driver_member',
  'economic_subject_instance',
] as const;
export type CommissioningDriverSummaryGrain =
  typeof COMMISSIONING_DRIVER_SUMMARY_GRAINS[number];

/**
 * Provenienza dell'attribuzione membro/cluster di una riga:
 *  - `snapshot`: ogni riga evento del gruppo portava il membro nello
 *    snapshot congelato al calcolo (fotografia alla data della riga);
 *  - `current`: almeno una riga evento è stata attribuita via join alla
 *    fotografia CORRENTE della gara (righe storiche senza membro nello snapshot).
 * Negli aggregati storici member_id è persistito, ma il cluster è letto
 * dalla fotografia corrente salvo puntatori espliciti nello snapshot.
 */
export const COMMISSIONING_DRIVER_SUMMARY_ATTRIBUTIONS = ['snapshot', 'current'] as const;
export type CommissioningDriverSummaryAttribution =
  typeof COMMISSIONING_DRIVER_SUMMARY_ATTRIBUTIONS[number];

const isoDate = z.string().date('Data valida in formato YYYY-MM-DD');

/** Parametri query del consuntivo per driver (validati al boundary HTTP). */
/** Existing event-ledger reader, including an optional proven economic owner. */
export const commissioningLedgerLinesQuerySchema = z.object({
  status: z.enum(COMMISSIONING_EVENT_LINE_STATUSES).optional(),
  configurator_instance_id: z.string().uuid().optional(),
  economic_subject_id: z.string().uuid().optional(),
  period_id: z.string().uuid().optional(),
  date_from: isoDate.optional(),
  date_to: isoDate.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
}).superRefine((value, ctx) => {
  if (value.date_from && value.date_to && value.date_from > value.date_to) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['date_to'], message: 'date_to deve essere >= date_from' });
  }
});
export type CommissioningLedgerLinesQuery = z.infer<typeof commissioningLedgerLinesQuerySchema>;

export const commissioningDriverSummaryQuerySchema = z
  .object({
    grain: z.enum(COMMISSIONING_DRIVER_SUMMARY_GRAINS).default('driver'),
    /** Periodo di competenza (righe: `period_id`; aggregati: `period_id`). */
    period_id: z.string().uuid().optional(),
    /**
     * Finestra di competenza per data (righe: `occurred_at` dell'evento;
     * aggregati: periodo che INTERSECA la finestra; aggregati senza periodo
     * esclusi quando la finestra è valorizzata).
     */
    date_from: isoDate.optional(),
    date_to: isoDate.optional(),
    /** false = le colonne WIP restano a zero (sole righe maturate). */
    include_wip: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .transform((v) => v === true || v === 'true')
      .default(true),
    /** Table controls only: totals still cover the complete authorized period. */
    search: z.string().trim().max(160).optional(),
    page: z.coerce.number().int().min(1).max(100_000).optional(),
    page_size: z.coerce.number().int().min(1).max(100).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.grain !== 'economic_subject_instance'
      && (v.page !== undefined || v.page_size !== undefined || v.search !== undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['grain'],
        message: 'Ricerca e paginazione sono disponibili nella vista soggetto × configuratore' });
    }
    if (v.date_from && v.date_to && v.date_from > v.date_to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_to'],
        message: 'date_to deve essere >= date_from',
      });
    }
  });
export type CommissioningDriverSummaryQuery =
  z.infer<typeof commissioningDriverSummaryQuerySchema>;

/** Importi e conteggi di un gruppo (riga, driver o totale gara). */
export const commissioningDriverSummaryAmountsSchema = z.object({
  /** € da righe evento maturate (confirmed+recredited). */
  event_amount_matured: z.number().finite(),
  /** € da righe evento in attesa (wip). */
  event_amount_wip: z.number().finite(),
  /** Quota incrementale € da risultati di periodo confermati. */
  aggregate_amount_matured: z.number().finite(),
  /** Quota incrementale € da risultati di periodo in attesa (wip). */
  aggregate_amount_wip: z.number().finite(),
  /** event_amount_matured + aggregate_amount_matured (calcolato una volta sola, nel service). */
  total_matured: z.number().finite(),
  /** event_amount_wip + aggregate_amount_wip. */
  total_wip: z.number().finite(),
  event_lines_matured: z.number().int().nonnegative(),
  event_lines_wip: z.number().int().nonnegative(),
  aggregate_results_matured: z.number().int().nonnegative(),
  aggregate_results_wip: z.number().int().nonnegative(),
}).strict();
export type CommissioningDriverSummaryAmounts =
  z.infer<typeof commissioningDriverSummaryAmountsSchema>;

export const commissioningEconomicCalculationStateSchema = z.enum([
  'calculated', 'pending', 'not_evaluable', 'excluded', 'unconfigured',
]);
export type CommissioningEconomicCalculationState = z.infer<typeof commissioningEconomicCalculationStateSchema>;

/** Stored period evidence, including superseded revisions. Never an extra award
 * to sum into the summary; threshold_state is informational only. */
export const commissioningEconomicPeriodEvidenceSchema = z.object({
  id: z.string().uuid(), instance_id: z.string().uuid(), economic_subject_id: z.string().uuid(),
  period_id: z.string().uuid().nullable(), period_label: z.string().nullable(),
  period_start: isoDate.nullable(), period_end: isoDate.nullable(), period_status: z.string().nullable(),
  kind: z.enum([...COMMISSIONING_AGGREGATE_RESULT_KINDS, 'threshold_state']),
  status: z.enum(COMMISSIONING_AGGREGATE_RESULT_STATUSES),
  is_current: z.boolean(), context_revision: z.number().int().positive().nullable(),
  rule_version: z.number().int().nonnegative().nullable(),
  metrics: z.record(z.number().finite()),
  indicator: z.number().finite().nullable(), band_id: z.string().nullable(),
  variant_source: z.string().nullable(), explanation: z.string().nullable(),
  condition_outcomes: z.record(z.enum(COMMISSIONING_FUNCTION_OUTCOMES)),
  computed_at: z.string(),
}).strict();
export type CommissioningEconomicPeriodEvidence = z.infer<typeof commissioningEconomicPeriodEvidenceSchema>;

/** Inventory of recorded contributions per period and contributor identity for
 * one owner × configurator. Consultable provenance only: in collective
 * maturation these are never personal payable shares of the subject's award. */
export const commissioningEconomicPeriodContributionSchema = z.object({
  instance_id: z.string().uuid(), economic_subject_id: z.string().uuid(),
  period_id: z.string().uuid().nullable(), period_label: z.string().nullable(),
  period_start: isoDate.nullable(), period_end: isoDate.nullable(),
  /** Identity saved on the row at processing time; null = contributor not recorded. */
  contributor_participant_id: z.string().uuid().nullable(),
  contributor_label: z.string().nullable(),
  context_revision: z.number().int().positive().nullable(),
  lines_matured: z.number().int().nonnegative(), lines_wip: z.number().int().nonnegative(),
  amount_matured: z.number().finite(), amount_wip: z.number().finite(),
}).strict();
export type CommissioningEconomicPeriodContribution = z.infer<typeof commissioningEconomicPeriodContributionSchema>;

const economicCoverageFields = {
  context_revision: z.number().int().positive(),
  configured: z.boolean(),
  issue: z.string().nullable(),
};
export const commissioningEconomicCoverageSchema = z.discriminatedUnion('basis', [
  z.object({ basis: z.literal('current_configuration'), ...economicCoverageFields }).strict(),
  z.object({
    basis: z.literal('period_closure'), ...economicCoverageFields,
    period_id: z.string().uuid(), closure_id: z.string().uuid(),
  }).strict(),
]);
export const commissioningEconomicCoverageStatusSchema = z.object({
  basis: z.enum(['current_configuration', 'period_closure', 'historical_unavailable']),
  period_id: z.string().uuid().nullable(),
  context_revision: z.number().int().positive().nullable(),
  closure_id: z.string().uuid().nullable(),
}).strict();
export type CommissioningEconomicCoverageStatus = z.infer<typeof commissioningEconomicCoverageStatusSchema>;

/**
 * Riga del consuntivo alla grana richiesta. Le chiavi non pertinenti alla
 * grana sono null (es. a grana `driver` istanza/cluster/membro sono null).
 * `driver_id` null = bucket Extra-driver.
 */
export const commissioningDriverSummaryRowSchema = commissioningDriverSummaryAmountsSchema.extend({
  driver_id: z.string().nullable(),
  driver_name: z.string().nullable(),
  instance_id: z.string().nullable(),
  instance_name: z.string().nullable(),
  /** null a grana cluster/membro = contributo non attribuibile a un cluster (scope istanza). */
  cluster_id: z.string().nullable(),
  cluster_name: z.string().nullable(),
  /** null a grana membro = contributo a scope istanza (nessun membro). */
  member_id: z.string().nullable(),
  member_label: z.string().nullable(),
  /** Valorizzata solo alle grane cluster/membro. */
  attribution: z.enum(COMMISSIONING_DRIVER_SUMMARY_ATTRIBUTIONS).nullable(),
  /** Only the common-contract economic grain carries these fields. Never a membership ID. */
  economic_subject_id: z.string().uuid().nullable().optional(),
  economic_subject_label: z.string().optional(),
  context_revisions: z.array(z.number().int().positive()).optional(),
  economic_state: commissioningEconomicCalculationStateSchema.optional(),
  economic_notes: z.array(z.string()).optional(),
  /** Frozen at closure when available; current coverage never reattributes history. */
  economic_coverage: commissioningEconomicCoverageSchema.optional(),
  period_evidence: z.array(commissioningEconomicPeriodEvidenceSchema).optional(),
  /** Same filters as the row; Σ amounts equal the row's event amounts. */
  period_contributions: z.array(commissioningEconomicPeriodContributionSchema).optional(),
});
export type CommissioningDriverSummaryRow =
  z.infer<typeof commissioningDriverSummaryRowSchema>;

/** Totale per driver (sempre presente, qualunque sia la grana). */
export const commissioningDriverSummaryDriverTotalSchema =
  commissioningDriverSummaryAmountsSchema.extend({
    /** null = bucket Extra-driver (istanze senza driver). */
    driver_id: z.string().nullable(),
    driver_name: z.string().nullable(),
    /** Configuratori distinti che hanno contribuito (righe evento o aggregati). */
    instance_count: z.number().int().nonnegative(),
  });
export type CommissioningDriverSummaryDriverTotal =
  z.infer<typeof commissioningDriverSummaryDriverTotalSchema>;

/** Risposta GET /incentivazione/ledger/competitions/:id/by-driver. */
export const commissioningDriverSummarySchema = z.object({
  competition_id: z.string(),
  economic_coverage_status: commissioningEconomicCoverageStatusSchema.optional(),
  family: z.enum(COMMISSIONING_COMPETITION_FAMILIES),
  grain: z.enum(COMMISSIONING_DRIVER_SUMMARY_GRAINS),
  filters: z.object({
    period_id: z.string().nullable(),
    date_from: z.string().nullable(),
    date_to: z.string().nullable(),
    include_wip: z.boolean(),
    search: z.string().optional(),
  }).strict(),
  /** Righe alla grana richiesta (ordinate: driver per nome, extra in coda). */
  rows: z.array(commissioningDriverSummaryRowSchema),
  pagination: z.object({
    page: z.number().int().positive(), page_size: z.number().int().min(1).max(100),
    total_rows: z.number().int().nonnegative(), total_pages: z.number().int().nonnegative(),
  }).strict().optional(),
  /** Totale per driver (solo driver reali, ordinati per nome). */
  drivers: z.array(commissioningDriverSummaryDriverTotalSchema),
  /** Bucket Extra-driver (driver_id null) — sempre presente, anche a zero. */
  extra: commissioningDriverSummaryDriverTotalSchema,
  /** Totale gara = Σ drivers + extra (composto una volta sola nel service). */
  total: commissioningDriverSummaryAmountsSchema,
}).strict();
export type CommissioningDriverSummary = z.infer<typeof commissioningDriverSummarySchema>;
